import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
} from 'react';
import { useMutation, useSubscription, useQuery } from '@apollo/client';
import { GameRoomUpdated, GameChatMessage } from '../gql/game/subscription';
import { GameRoomById } from '../gql/game/query';
import {
  CreateGameRoom,
  JoinGameRoom,
  StartRound,
  AcknowledgeRole,
  SubmitClue,
  AdvanceToVoting,
  CastVote,
  ImpostorGuess,
  LeaveGameRoom,
  RejoinGameRoom,
  EndGame,
  SendGameMessage,
} from '../gql/game/mutation';
import { WORD_PACKS } from '../utils/gameWords';
import { AuthContext } from './AuthContext';
import { FindOneUserQuery } from '../gql/user/query';

export const GameContext = createContext(null);

// ─── Local (offline) state machine ───────────────────────────────────────────

function createLocalGame(pack) {
  const words = WORD_PACKS[pack] ?? WORD_PACKS.everyday;
  return {
    id: 'local',
    code: 'LOCAL',
    hostId: null,
    status: 'IN_PROGRESS',
    mode: 'LOCAL',
    pack,
    players: [],
    currentRound: null,
    rounds: [],
    _localPack: pack,
    _localWords: words,
  };
}

// ─── Provider ─────────────────────────────────────────────────────────────────

export function GameProvider({ children }) {
  const { authState } = useContext(AuthContext);

  const { data: meData } = useQuery(FindOneUserQuery, {
    variables: { email: authState?.email },
    skip: !authState?.email || !authState?.authenticated,
  });
  const currentUserId = meData?.findOneUser?.id ?? null;

  const [room, setRoom] = useState(null);
  const [messages, setMessages] = useState([]);
  const [localPlayers, setLocalPlayers] = useState([]);
  const [localRoundIndex, setLocalRoundIndex] = useState(0);

  const roomId = room?.id && room.id !== 'local' ? room.id : null;
  const currentRoundIdRef = useRef(null);

  // ── Polling fallback: catches room state missed during WS reconnects ─────
  useQuery(GameRoomById, {
    variables: { roomId },
    skip: !roomId,
    pollInterval: 5000,
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.gameRoomById) setRoom(data.gameRoomById);
    },
  });

  // ── Subscription: room state ─────────────────────────────────────────────

  useSubscription(GameRoomUpdated, {
    variables: { roomId },
    skip: !roomId,
    onData: ({ data }) => {
      if (data?.data?.gameRoomUpdated) {
        const newRoom = data.data.gameRoomUpdated;
        const newRoundId = newRoom?.currentRound?.id;
        if (
          newRoundId &&
          newRoundId !== currentRoundIdRef.current &&
          newRoom.currentRound?.status === 'ROLE_REVEAL'
        ) {
          setMessages([]);
        }
        if (newRoundId) currentRoundIdRef.current = newRoundId;
        setRoom(newRoom);
      }
    },
  });

  // ── Subscription: chat messages ──────────────────────────────────────────

  useSubscription(GameChatMessage, {
    variables: { roomId },
    skip: !roomId || room?.mode !== 'REMOTE',
    onData: ({ data }) => {
      if (data?.data?.gameChatMessage) {
        setMessages((prev) => [...prev, data.data.gameChatMessage]);
      }
    },
  });

  // ── Mutations ────────────────────────────────────────────────────────────

  const [_createGameRoom] = useMutation(CreateGameRoom);
  const [_joinGameRoom] = useMutation(JoinGameRoom);
  const [_startRound] = useMutation(StartRound);
  const [_acknowledgeRole] = useMutation(AcknowledgeRole);
  const [_submitClue] = useMutation(SubmitClue);
  const [_advanceToVoting] = useMutation(AdvanceToVoting);
  const [_castVote] = useMutation(CastVote);
  const [_impostorGuess] = useMutation(ImpostorGuess);
  const [_leaveGameRoom] = useMutation(LeaveGameRoom);
  const [_rejoinGameRoom] = useMutation(RejoinGameRoom);
  const [_endGame] = useMutation(EndGame);
  const [_sendGameMessage] = useMutation(SendGameMessage);

  // ── Online actions ───────────────────────────────────────────────────────

  const createRoom = useCallback(async (pack, mode) => {
    const { data } = await _createGameRoom({ variables: { pack, mode } });
    setRoom(data.createGameRoom);
    setMessages([]);
    return data.createGameRoom;
  }, [_createGameRoom]);

  const joinRoom = useCallback(async (code) => {
    const { data } = await _joinGameRoom({ variables: { code } });
    setRoom(data.joinGameRoom);
    setMessages([]);
    return data.joinGameRoom;
  }, [_joinGameRoom]);

  const startRound = useCallback(async () => {
    if (!room) return;
    const { data } = await _startRound({ variables: { roomId: room.id } });
    setRoom(data.startRound);
    setMessages([]);
  }, [room, _startRound]);

  const acknowledgeRole = useCallback(async () => {
    if (!room?.currentRound) return;
    const { data } = await _acknowledgeRole({ variables: { roundId: room.currentRound.id } });
    setRoom(data.acknowledgeRole);
  }, [room, _acknowledgeRole]);

  const submitClue = useCallback(async (word) => {
    if (!room?.currentRound) return;
    const { data } = await _submitClue({ variables: { roundId: room.currentRound.id, word } });
    setRoom(data.submitClue);
  }, [room, _submitClue]);

  const advanceToVoting = useCallback(async () => {
    if (!room?.currentRound) return;
    const { data } = await _advanceToVoting({ variables: { roundId: room.currentRound.id } });
    setRoom(data.advanceToVoting);
  }, [room, _advanceToVoting]);

  const castVote = useCallback(async (targetId) => {
    if (!room?.currentRound) return;
    const { data } = await _castVote({ variables: { roundId: room.currentRound.id, targetId } });
    setRoom(data.castVote);
  }, [room, _castVote]);

  const impostorGuess = useCallback(async (guess) => {
    if (!room?.currentRound) return;
    const { data } = await _impostorGuess({ variables: { roundId: room.currentRound.id, guess } });
    setRoom(data.impostorGuess);
  }, [room, _impostorGuess]);

  const leaveRoom = useCallback(async () => {
    if (!room) return;
    if (room.id !== 'local') {
      await _leaveGameRoom({ variables: { roomId: room.id } });
    }
    setRoom(null);
    setMessages([]);
    setLocalPlayers([]);
  }, [room, _leaveGameRoom]);

  const rejoinRoom = useCallback(async () => {
    if (!room) return;
    const { data } = await _rejoinGameRoom({ variables: { roomId: room.id } });
    setRoom(data.rejoinGameRoom);
  }, [room, _rejoinGameRoom]);

  const endGame = useCallback(async () => {
    if (!room) return;
    if (room.id !== 'local') {
      const { data } = await _endGame({ variables: { roomId: room.id } });
      setRoom(data.endGame);
    } else {
      setRoom(null);
    }
    setMessages([]);
  }, [room, _endGame]);

  const sendMessage = useCallback(async ({ text, audioUrl, duration }) => {
    if (!room) return;
    await _sendGameMessage({ variables: { roomId: room.id, text, audioUrl, duration } });
  }, [room, _sendGameMessage]);

  // ── Local (offline) actions ───────────────────────────────────────────────

  const startLocalGame = useCallback((pack, players) => {
    const game = createLocalGame(pack);
    setLocalPlayers(players.map((p) => ({ ...p, score: 0 })));
    setRoom(game);
    setLocalRoundIndex(0);
  }, []);

  const startLocalRound = useCallback(() => {
    if (!room || room.mode !== 'LOCAL') return;
    const words = room._localWords;
    const word = words[Math.floor(Math.random() * words.length)];
    const impostorIdx = Math.floor(Math.random() * localPlayers.length);
    const roundNumber = (room.rounds?.length ?? 0) + 1;
    const round = {
      id: `local-round-${roundNumber}`,
      roundNumber,
      secretWord: word,
      impostorId: impostorIdx,
      status: 'ROLE_REVEAL',
      outcome: null,
      clues: [],
      votes: [],
    };
    setRoom((prev) => ({ ...prev, currentRound: round, status: 'IN_PROGRESS' }));
  }, [room, localPlayers]);

  const submitLocalClue = useCallback((playerIdx, word) => {
    setRoom((prev) => {
      if (!prev?.currentRound) return prev;
      const clue = {
        id: `clue-${prev.currentRound.clues.length}`,
        playerId: playerIdx,
        word,
        order: prev.currentRound.clues.length,
      };
      const clues = [...prev.currentRound.clues, clue];
      const status = clues.length >= localPlayers.length ? 'DISCUSSION' : 'CLUE_PHASE';
      return {
        ...prev,
        currentRound: { ...prev.currentRound, clues, status },
      };
    });
  }, [localPlayers]);

  const advanceLocalPhase = useCallback((nextStatus) => {
    setRoom((prev) => {
      if (!prev?.currentRound) return prev;
      return { ...prev, currentRound: { ...prev.currentRound, status: nextStatus } };
    });
  }, []);

  const applyLocalScores = useCallback((impostorId, outcome) => {
    setLocalPlayers((prev) => prev.map((p, i) => {
      const isImpostor = i === impostorId;
      let delta = 0;
      if (outcome === 'VILLAGERS_WIN') delta = isImpostor ? 0 : 2;
      else if (outcome === 'IMPOSTOR_WIN_GUESSED') delta = isImpostor ? 3 : 0;
      else if (outcome === 'IMPOSTOR_WIN_WRONG_VOTE') delta = isImpostor ? 4 : 0;
      return { ...p, score: (p.score ?? 0) + delta };
    }));
  }, []);

  const castLocalVote = useCallback((voterId, targetId) => {
    setRoom((prev) => {
      if (!prev?.currentRound) return prev;
      const vote = { id: `vote-${voterId}`, roundId: prev.currentRound.id, voterId, targetId };
      const votes = [...prev.currentRound.votes, vote];

      if (votes.length >= localPlayers.length) {
        const tally = {};
        for (const v of votes) {
          tally[v.targetId] = (tally[v.targetId] ?? 0) + 1;
        }
        const maxVotes = Math.max(...Object.values(tally));
        const eliminated = parseInt(Object.entries(tally).find(([, c]) => c === maxVotes)[0]);
        const status = eliminated === prev.currentRound.impostorId ? 'IMPOSTOR_GUESS' : 'FINISHED';
        const outcome = status === 'FINISHED' ? 'IMPOSTOR_WIN_WRONG_VOTE' : null;
        if (outcome) applyLocalScores(prev.currentRound.impostorId, outcome);
        return { ...prev, currentRound: { ...prev.currentRound, votes, status, outcome } };
      }
      return { ...prev, currentRound: { ...prev.currentRound, votes } };
    });
  }, [localPlayers, applyLocalScores]);

  const resolveLocalRound = useCallback((outcome) => {
    setRoom((prev) => {
      if (!prev?.currentRound) return prev;
      applyLocalScores(prev.currentRound.impostorId, outcome);
      const finishedRound = { ...prev.currentRound, status: 'FINISHED', outcome };
      return {
        ...prev,
        currentRound: finishedRound,
        rounds: [...(prev.rounds ?? []), finishedRound],
      };
    });
  }, [applyLocalScores]);

  // ── Derived helpers ───────────────────────────────────────────────────────

  const isHost = room?.hostId === currentUserId;
  const myPlayer = room?.players?.find((p) => p.userId === currentUserId);
  const currentRound = room?.currentRound ?? null;
  const amImpostor = currentRound
    ? room.mode === 'LOCAL'
      ? false // Local: managed by caller
      : currentRound.impostorId === currentUserId
    : false;
  const mySecretWord = amImpostor ? null : currentRound?.secretWord ?? null;

  const onlinePlayers = room?.players?.filter((p) => p.online) ?? [];
  const myClueIndex = currentRound
    ? currentRound.clues.length % Math.max(onlinePlayers.length, 1)
    : 0;
  const isMyClutTurn =
    currentRound?.status === 'CLUE_PHASE' &&
    onlinePlayers[myClueIndex]?.userId === currentUserId;

  const hasVoted = currentRound?.votes?.some((v) => v.voterId === currentUserId) ?? false;

  return (
    <GameContext.Provider
      value={{
        room,
        setRoom,
        messages,
        localPlayers,
        isHost,
        myPlayer,
        currentRound,
        amImpostor,
        mySecretWord,
        onlinePlayers,
        isMyClutTurn,
        hasVoted,
        currentUserId,
        // Online actions
        createRoom,
        joinRoom,
        startRound,
        acknowledgeRole,
        submitClue,
        advanceToVoting,
        castVote,
        impostorGuess,
        leaveRoom,
        rejoinRoom,
        endGame,
        sendMessage,
        // Local actions
        startLocalGame,
        startLocalRound,
        submitLocalClue,
        advanceLocalPhase,
        castLocalVote,
        resolveLocalRound,
      }}
    >
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const ctx = useContext(GameContext);
  if (!ctx) throw new Error('useGame must be used inside GameProvider');
  return ctx;
}
